"use client";

// Виджет «База знаний» (вкладка «Задачи»): личные полезные ссылки с описанием,
// сгруппированы по разделам (сворачиваемым). Личная база (только владелец).
// Компактный блок «раздвигается вниз» поверх доски (тело — absolute, не двигает
// раскладку колонки), как единый растущий блок, а не отдельный поповер.

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ExternalLink, Film, Image as ImageIcon, Play } from "lucide-react";
import { IconBook, IconCheck, IconChevronDown, IconChevronRight, IconClose, IconEdit, IconPlus, IconTrash } from "@/components/ui/icons";

import { api } from "@/lib/api";
import type { KbDocMeta, KbDocScope, KbLink } from "@/lib/types";
import { KbDocModal } from "@/components/kb/kb-doc-modal";
import { DocTree, buildDocTree } from "@/components/kb/doc-tree";
import { AuthImage } from "@/components/ui/auth-image";
import { AuthVideo } from "@/components/ui/auth-video";
import { useOutsideClick } from "@/lib/use-outside-click";
import { INPUT_CLS, TEXTAREA_CLS } from "@/components/ui/input";
import { Z } from "@/lib/z";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/ui/states";
import { useStickyState } from "@/lib/use-sticky-state";

const inputCls = INPUT_CLS;
const NO_CAT = "Без раздела";

// Превью прикреплённого медиа: маленькая миниатюра; наведение → увеличенное
// превью (картинка), клик → лайтбокс на весь экран.
function MediaThumb({ url, type }: { url: string; type: "" | "image" | "video" }) {
  const isVideo = type === "video";
  const [hover, setHover] = useState<DOMRect | null>(null);
  const [open, setOpen] = useState(false);
  const previewPos = (r: DOMRect): React.CSSProperties => {
    const w = 280;
    let left = r.right + 8;
    if (left + w > window.innerWidth - 8) left = r.left - w - 8;
    if (left < 8) left = 8;
    let top = r.top - 16;
    if (top + 220 > window.innerHeight - 8) top = window.innerHeight - 8 - 220;
    if (top < 8) top = 8;
    return { position: "fixed", left, top, width: w, zIndex: Z.DROPDOWN };
  };
  return (
    <>
      <button type="button" data-tooltip="Открыть"
        onMouseEnter={isVideo ? undefined : (e) => setHover(e.currentTarget.getBoundingClientRect())}
        onMouseLeave={isVideo ? undefined : () => setHover(null)}
        onClick={() => { setHover(null); setOpen(true); }}
        className="relative h-12 w-16 shrink-0 overflow-hidden rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] transition hover:border-[var(--primary)]">
        {isVideo
          ? <AuthVideo src={url} controls={false} muted className="h-full w-full object-cover" />
          : <AuthImage src={url} className="h-full w-full object-cover" />}
        {isVideo && <span className="pointer-events-none absolute inset-0 grid place-items-center bg-black/25"><Play size={16} className="text-white" /></span>}
      </button>
      {hover && typeof document !== "undefined" && createPortal(
        <div className="pointer-events-none overflow-hidden rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] p-1 elev-modal" style={previewPos(hover)}>
          <AuthImage src={url} className="max-h-[210px] w-full rounded-[var(--radius-md)] object-contain" />
        </div>, document.body)}
      {open && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 grid place-items-center bg-black/75 p-6" style={{ zIndex: Z.LIGHTBOX }} onClick={() => setOpen(false)}>
          <button type="button" onClick={() => setOpen(false)} className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full bg-white/15 text-white hover:bg-white/25"><IconClose size={18} /></button>
          <div onClick={(e) => e.stopPropagation()} className="max-h-[90dvh] max-w-[92vw]">
            {isVideo
              ? <AuthVideo src={url} className="max-h-[90dvh] max-w-[92vw] rounded-[var(--radius-md)]" />
              : <AuthImage src={url} className="max-h-[90dvh] max-w-[92vw] rounded-[var(--radius-md)] object-contain" />}
          </div>
        </div>, document.body)}
    </>
  );
}

export function KnowledgeBaseWidget({ толькоСсылки = false }: { толькоСсылки?: boolean } = {}) {
  // ⚠️ В КАБИНЕТЕ — ТОЛЬКО ССЫЛКИ (Максим, 04.09.2026: «база знаний у меня
  // должна быть только со ссылками, без материалов, которые находятся в обычной
  // базе знаний»). Документы живут в разделе «База знаний» и дублировать их в
  // кабинете незачем; ссылки же — рабочий инструмент под рукой.
  const [links, setLinks] = useState<KbLink[]>([]);
  const [docs, setDocs] = useState<KbDocMeta[]>([]);
  const [openDoc, setOpenDoc] = useState<{ id?: number; scope?: KbDocScope; category?: string; parentId?: number; rect?: DOMRect } | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());  // свёрнутые разделы ссылок
  const [docCollapsed, setDocCollapsed] = useState<Set<string>>(new Set());  // свёрнутые разделы документов (ключ scope::раздел)
  const [treeExpanded, setTreeExpanded] = useState<Set<number>>(new Set());  // раскрытые папки в дереве (по id, по умолчанию свёрнуто)
  const toggleTree = (id: number) => setTreeExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // Drag-and-drop переноса между разделами: тащим карточку → бросаем на заголовок раздела.
  const [drag, setDrag] = useState<{ kind: "link" | "doc"; id: number; from: string } | null>(null);
  const [overCat, setOverCat] = useState<string | null>(null);  // ключ раздела под курсором (L::имя / D::scope::имя)
  const endDrag = () => { setDrag(null); setOverCat(null); };
  async function moveLinkToCat(id: number, catName: string) {
    endDrag();
    try { await api.kbPatchLink(id, { category: catName === NO_CAT ? "" : catName }); await reload(); } catch { /* тихо */ }
  }
  async function moveDocToCat(id: number, scope: KbDocScope, catName: string) {
    endDrag();
    try { await api.kbPatchDoc(id, { category: catName === NO_CAT ? "" : catName, scope, parent_id: null }); await reload(); } catch { /* тихо */ }
  }
  const [editing, setEditing] = useState<null | "new" | number>(null);
  const [form, setForm] = useState({ category: "", title: "", url: "", description: "", media_url: "", media_type: "" as "" | "image" | "video", media_caption: "" });
  const imgFileRef = useRef<HTMLInputElement>(null);
  const vidFileRef = useRef<HTMLInputElement>(null);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [tabХранимая, setTab] = useStickyState<"docs" | "links">("kb:tab", "docs");
  const tab = толькоСсылки ? "links" : tabХранимая;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const reload = () => {
    api.kbListLinks().then(setLinks).catch(() => {});
    api.kbListDocs().then(setDocs).catch(() => {});
  };
  useEffect(() => { void reload(); }, []);

  // Документы: группировка по области (Личные/Командные) → раздел.
  const docGroups = useMemo(() => {
    const groupCats = (arr: KbDocMeta[]) => {
      const order: string[] = []; const m = new Map<string, KbDocMeta[]>();
      for (const d of arr) { const c = d.category || NO_CAT; if (!m.has(c)) { m.set(c, []); order.push(c); } m.get(c)!.push(d); }
      return order.map((name) => ({ name, items: m.get(name)! }));
    };
    return ([
      { scope: "personal" as KbDocScope, label: "Личные", cats: groupCats(docs.filter((d) => d.scope === "personal")) },
      { scope: "team" as KbDocScope, label: "Командные", cats: groupCats(docs.filter((d) => d.scope === "team")) },
    ]).filter((g) => g.cats.length);
  }, [docs]);
  const allCats = useMemo(() => [...new Set([...docs.map((d) => d.category), ...links.map((l) => l.category)].filter(Boolean))], [docs, links]);

  // Закрытие по клику мимо блока (тело перекрывает доску).
  useOutsideClick(wrapRef, () => { setOpen(false); setEditing(null); }, open);

  // Группировка по разделам (в порядке появления).
  const cats = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, KbLink[]>();
    for (const l of links) {
      const c = l.category || NO_CAT;
      if (!map.has(c)) { map.set(c, []); order.push(c); }
      map.get(c)!.push(l);
    }
    return order.map((c) => ({ name: c, items: map.get(c)! }));
  }, [links]);
  const catNames = useMemo(() => [...new Set(links.map((l) => l.category).filter(Boolean))], [links]);

  function startNew() { setForm({ category: "", title: "", url: "", description: "", media_url: "", media_type: "", media_caption: "" }); setEditing("new"); }
  function startEdit(l: KbLink) { setForm({ category: l.category, title: l.title, url: l.url, description: l.description, media_url: l.media_url, media_type: l.media_type, media_caption: l.media_caption }); setEditing(l.id); }
  async function save() {
    if (!form.title.trim() && !form.url.trim() && !form.media_url) return;
    try {
      if (editing === "new") await api.kbCreateLink(form);
      else if (typeof editing === "number") await api.kbPatchLink(editing, form);
      setEditing(null); await reload();
    } catch { /* тихо */ }
  }
  async function uploadMedia(files: FileList | null, kind: "image" | "video") {
    const f = files?.[0]; if (!f) return;
    setMediaUploading(true);
    try { const r = await api.kbUploadFile(f); setForm((p) => ({ ...p, media_url: r.url, media_type: (r.type ?? kind) })); }
    catch { /* тихо */ } finally { setMediaUploading(false); if (imgFileRef.current) imgFileRef.current.value = ""; if (vidFileRef.current) vidFileRef.current.value = ""; }
  }
  async function del(id: number) { try { await api.kbDeleteLink(id); await reload(); } catch { /* тихо */ } }
  // Переименование раздела = bulk-обновление category всех записей в нём.
  async function renameLinkCat(cat: { name: string; items: KbLink[] }) {
    const cur = cat.name === NO_CAT ? "" : cat.name;
    const next = window.prompt("Название раздела ссылок:", cur);
    if (next === null || next.trim() === cur) return;
    try { for (const l of cat.items) await api.kbPatchLink(l.id, { category: next.trim() }); await reload(); } catch { /* тихо */ }
  }
  async function renameDocCat(cat: { name: string; items: KbDocMeta[] }) {
    const cur = cat.name === NO_CAT ? "" : cat.name;
    const next = window.prompt("Название раздела документов:", cur);
    if (next === null || next.trim() === cur) return;
    try { for (const d of cat.items) await api.kbPatchDoc(d.id, { category: next.trim() }); await reload(); } catch { /* тихо */ }
  }
  const toggleCat = (c: string) => setCollapsed((p) => { const n = new Set(p); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const toggleDocCat = (k: string) => setDocCollapsed((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div ref={wrapRef} className="relative">
      {/* Шапка-блок (всегда видна). При раскрытии низ срезан — стыкуется с телом. */}
      <button type="button" onClick={() => { const nv = !open; setOpen(nv); setEditing(null); if (nv) { setCollapsed(new Set(cats.map((c) => c.name))); setDocCollapsed(new Set(docGroups.flatMap((g) => g.cats.map((c) => `${g.scope}::${c.name}`)))); } }}
        className={`glass-panel flex w-full flex-col rounded-[var(--radius-lg)] border border-[var(--border)] p-4 text-left transition hover:border-[var(--primary)] ${open ? "relative z-30 rounded-t-none border-t-0" : ""}`}>
        <div className="flex items-center gap-2">
          <span className="grid shrink-0 place-items-center rounded-[var(--radius-md)]" style={{ width: 30, height: 30, background: "color-mix(in srgb, #6366f1 16%, var(--card))", color: "#6366f1" }}>
            <IconBook size={16} />
          </span>
          <span className="truncate text-[12px] font-bold text-[var(--text)]" >База знаний</span>
          <Badge color="#6366f1" className="ml-auto tabular-nums">{толькоСсылки ? links.length : links.length + docs.length}</Badge>
          <IconChevronRight size={16} className={`shrink-0 text-[var(--textMuted)] transition-transform ${open ? "-rotate-90" : ""}`} />
        </div>
        <div className="mt-1 text-[11px] text-[var(--textMuted)]">{толькоСсылки ? "Ссылки по разделам. Документы — в разделе «База знаний»." : "Документы и материалы по разделам."}</div>
      </button>

      {/* Тело — выезжает ВВЕРХ ПОВЕРХ контента (absolute, не растягивает колонку).
          Плавно раскрывается/сворачивается через переходы max-height/opacity. */}
      <div className={`absolute left-0 right-0 bottom-full z-30 origin-bottom overflow-y-auto scrollbar-autohide rounded-t-2xl border border-b-0 border-[var(--border)] bg-[var(--surface)] p-3 shadow-[0_-20px_48px_rgba(0,0,0,0.3)] transition-[max-height,opacity,transform] duration-200 ease-out ${open ? "max-h-[64dvh] translate-y-0 opacity-100" : "pointer-events-none max-h-0 translate-y-1 opacity-0"}`}>
          {/* Переключатель Документы / Ссылки + добавление активного типа */}
          <div className="mb-2.5 flex items-center gap-2">
            <span className="seg-group">
              {(толькоСсылки
                ? ([["links", "Ссылки", links.length]] as const)
                : ([["docs", "Документы", docs.length], ["links", "Материалы", links.length]] as const)
              ).map(([k, label, n]) => (
                <button key={k} type="button" onClick={() => { setTab(k); setEditing(null); }} style={{ boxShadow: "none" }}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold transition ${tab === k ? "seg seg-on" : "seg"}`}>
                  {label}<span className={`tabular-nums ${tab === k ? "opacity-80" : "opacity-50"}`}>{n}</span>
                </button>
              ))}
            </span>
            <span className="flex-1" />
            {tab === "docs" && !толькоСсылки ? (
              <Button onClick={(e) => setOpenDoc({ scope: "personal", category: "", rect: e.currentTarget.getBoundingClientRect() })}>
                <IconPlus size={12} /> документ
              </Button>
            ) : (
              <Button onClick={startNew}>
                <IconPlus size={12} /> материал
              </Button>
            )}
          </div>

          {/* Документы */}
          {tab === "docs" && !толькоСсылки && (
            <div className="flex flex-col gap-2">
              {docGroups.map((g) => (
                <div key={g.scope}>
                  <div className="mb-1 text-[11px] font-semibold text-[var(--textMuted)]/70">{g.label}</div>
                  <div className="flex flex-col gap-1.5">
                    {g.cats.map((cat) => {
                      const ck = `${g.scope}::${cat.name}`;
                      const catOpen = !docCollapsed.has(ck);
                      return (
                        <div key={ck}>
                          <div
                            onDragOver={drag?.kind === "doc" ? (e) => { e.preventDefault(); setOverCat(`D::${ck}`); } : undefined}
                            onDragLeave={drag?.kind === "doc" ? () => setOverCat((c) => (c === `D::${ck}` ? null : c)) : undefined}
                            onDrop={drag?.kind === "doc" ? () => void moveDocToCat(drag.id, g.scope, cat.name) : undefined}
                            className={`group/cat flex items-center gap-1 rounded-[var(--radius-md)] transition ${overCat === `D::${ck}` ? "ring-2 ring-[var(--primary)] bg-[color-mix(in_srgb,var(--primary)_8%,transparent)]" : ""}`}>
                            <button type="button" onClick={() => toggleDocCat(ck)}
                              className="glass-flat flex min-w-0 flex-1 items-center gap-1.5 rounded-[var(--radius-md)] px-1 py-1 text-left hover:bg-[var(--hover)]">
                              {catOpen ? <IconChevronDown size={13} className="shrink-0 text-[var(--textMuted)]" /> : <IconChevronRight size={13} className="shrink-0 text-[var(--textMuted)]" />}
                              <span className="section-title min-w-0 truncate">{cat.name}</span>
                              <span className="flex-1" />
                              <span className="shrink-0 text-[11px] tabular-nums text-[var(--textMuted)]">{cat.items.length}</span>
                            </button>
                            <button type="button" data-tooltip="Переименовать раздел" style={{ boxShadow: "none" }}
                              onClick={() => void renameDocCat(cat)}
                              className="grid h-6 w-6 shrink-0 place-items-center rounded-[var(--radius-md)] text-[var(--textMuted)] opacity-0 transition hover:text-[var(--primary)] group-hover/cat:opacity-100"><IconEdit size={12} /></button>
                            <button type="button" data-tooltip="Добавить документ в раздел" style={{ boxShadow: "none" }}
                              onClick={(e) => setOpenDoc({ scope: g.scope, category: cat.name === NO_CAT ? "" : cat.name, rect: e.currentTarget.getBoundingClientRect() })}
                              className="grid h-6 w-6 shrink-0 place-items-center rounded-[var(--radius-md)] text-[var(--textMuted)] opacity-0 transition hover:text-[var(--primary)] group-hover/cat:opacity-100">
                              <IconPlus size={13} />
                            </button>
                          </div>
                          {catOpen && (
                            <div className="ml-2 pt-0.5">
                              <DocTree nodes={buildDocTree(cat.items)} expanded={treeExpanded} onToggle={toggleTree}
                                onOpen={(d, rect) => setOpenDoc({ id: d.id, rect })}
                                onAddChild={(parentId, rect) => setOpenDoc({ parentId, rect })}
                                onDocDragStart={(d) => setDrag({ kind: "doc", id: d.id, from: `${g.scope}::${d.category || NO_CAT}` })}
                                onDocDragEnd={endDrag} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {docs.length === 0 && <div className="rounded-[var(--radius-md)] border border-dashed border-[var(--border)] py-3 text-center text-[11px] text-[var(--textMuted)]">Документов пока нет — создайте первый</div>}
            </div>
          )}

          {tab === "links" && (<>
          {/* Форма добавления/редактирования */}
          {editing !== null && (
            <div className="mb-3 space-y-1.5 rounded-[var(--radius-md)] border border-[var(--primary)]/40 bg-[color-mix(in_srgb,var(--primary)_5%,transparent)] p-2.5">
              <input className={inputCls} list="kb-cats" placeholder="Раздел (напр. Документация)" value={form.category} onChange={(e) => set("category", e.target.value)} />
              <datalist id="kb-cats">{catNames.map((c) => <option key={c} value={c} />)}</datalist>
              <input className={inputCls} placeholder="Название" value={form.title} onChange={(e) => set("title", e.target.value)} autoFocus />
              <input className={inputCls} placeholder="Ссылка (https://…)" value={form.url} onChange={(e) => set("url", e.target.value)} />
              <textarea className={`${TEXTAREA_CLS} min-h-[34px] resize-y`} rows={2} placeholder="Описание (необязательно)" value={form.description} onChange={(e) => set("description", e.target.value)} />
              {/* Прикреплённое фото/видео */}
              {form.media_url ? (
                <>
                  <div className="relative rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] p-1">
                    {form.media_type === "video"
                      ? <AuthVideo src={form.media_url} className="max-h-44 w-full rounded-[var(--radius-md)]" />
                      : <AuthImage src={form.media_url} className="max-h-44 w-full rounded-[var(--radius-md)] object-contain" />}
                    <button type="button" onClick={() => setForm((p) => ({ ...p, media_url: "", media_type: "", media_caption: "" }))}
                      className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-[var(--danger)] text-white" data-tooltip="Убрать медиа"><IconClose size={11} /></button>
                  </div>
                  <input className={inputCls} placeholder="Подпись к фото/видео (необязательно)" value={form.media_caption} onChange={(e) => set("media_caption", e.target.value)} />
                </>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button type="button" onClick={() => imgFileRef.current?.click()} disabled={mediaUploading}
                    className="inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border)] px-2 py-1 text-[11px] font-semibold text-[var(--textMuted)] hover:border-[var(--primary)] hover:text-[var(--primary)] disabled:opacity-50"><ImageIcon size={12} /> Фото</button>
                  <button type="button" onClick={() => vidFileRef.current?.click()} disabled={mediaUploading}
                    className="inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border)] px-2 py-1 text-[11px] font-semibold text-[var(--textMuted)] hover:border-[var(--primary)] hover:text-[var(--primary)] disabled:opacity-50"><Film size={12} /> Видео</button>
                  {mediaUploading && <span className="text-[11px] text-[var(--textMuted)]">загрузка…</span>}
                </div>
              )}
              <input ref={imgFileRef} type="file" accept="image/*" hidden onChange={(e) => void uploadMedia(e.target.files, "image")} />
              <input ref={vidFileRef} type="file" accept="video/*" hidden onChange={(e) => void uploadMedia(e.target.files, "video")} />
              <div className="flex items-center gap-2 pt-0.5">
                <Button variant="primary" onClick={() => void save()} disabled={!form.title.trim() && !form.url.trim() && !form.media_url}>
                  <IconCheck size={12} /> Сохранить
                </Button>
                <Button onClick={() => setEditing(null)}>Отмена</Button>
              </div>
            </div>
          )}

          {/* Разделы со ссылками */}
          <div className="flex flex-col gap-1.5">
            {cats.map((cat) => {
              const catOpen = !collapsed.has(cat.name);
              return (
                <div key={cat.name}>
                  <div
                    onDragOver={drag?.kind === "link" ? (e) => { e.preventDefault(); setOverCat(`L::${cat.name}`); } : undefined}
                    onDragLeave={drag?.kind === "link" ? () => setOverCat((c) => (c === `L::${cat.name}` ? null : c)) : undefined}
                    onDrop={drag?.kind === "link" ? () => { if (drag.from !== cat.name) void moveLinkToCat(drag.id, cat.name); else endDrag(); } : undefined}
                    className={`group/lcat flex items-center gap-1 rounded-[var(--radius-md)] transition ${overCat === `L::${cat.name}` ? "ring-2 ring-[var(--primary)] bg-[color-mix(in_srgb,var(--primary)_8%,transparent)]" : ""}`}>
                    <button type="button" onClick={() => toggleCat(cat.name)}
                      className="glass-flat flex min-w-0 flex-1 items-center gap-1.5 rounded-[var(--radius-md)] px-1 py-1 text-left hover:bg-[var(--hover)]">
                      {catOpen ? <IconChevronDown size={13} className="shrink-0 text-[var(--textMuted)]" /> : <IconChevronRight size={13} className="shrink-0 text-[var(--textMuted)]" />}
                      <span className="section-title min-w-0 truncate">{cat.name}</span>
                      <span className="flex-1" />
                      <span className="shrink-0 text-[11px] tabular-nums text-[var(--textMuted)]">{cat.items.length}</span>
                    </button>
                    <button type="button" data-tooltip="Переименовать раздел" style={{ boxShadow: "none" }}
                      onClick={() => void renameLinkCat(cat)}
                      className="grid h-6 w-6 shrink-0 place-items-center rounded-[var(--radius-md)] text-[var(--textMuted)] opacity-0 transition hover:text-[var(--primary)] group-hover/lcat:opacity-100"><IconEdit size={12} /></button>
                  </div>
                  {catOpen && (
                    <div className="ml-4 flex flex-col gap-1.5 border-l border-[var(--border)] pl-3 pt-1">
                      {cat.items.map((l) => (
                        <div key={l.id} draggable
                          onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; setDrag({ kind: "link", id: l.id, from: cat.name }); }}
                          onDragEnd={endDrag}
                          className="flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] px-2.5 py-1.5 cursor-grab active:cursor-grabbing">
                          <div className="min-w-0 flex-1">
                            {l.url ? (
                              <a href={l.url} target="_blank" rel="noreferrer" draggable={false} className="section-title inline-flex max-w-full items-center gap-1 hover:text-[var(--primary)]" data-tooltip={l.url}>
                                <ExternalLink size={11} className="shrink-0" /><span className="truncate">{l.title || l.url}</span>
                              </a>
                            ) : <span className="section-title">{l.title}</span>}
                            {l.description && <div className="mt-0.5 text-[11px] leading-snug text-[var(--textMuted)]">{l.description}</div>}
                            {l.media_url && (
                              <div className="mt-1.5 flex items-center gap-2.5">
                                <MediaThumb url={l.media_url} type={l.media_type} />
                                {l.media_caption && <div className="min-w-0 flex-1 text-[11px] leading-snug text-[var(--textMuted)]">{l.media_caption}</div>}
                              </div>
                            )}
                          </div>
                          <button type="button" onClick={() => startEdit(l)} data-tooltip="Редактировать" className="shrink-0 text-[var(--textMuted)]/60 hover:text-[var(--primary)]"><IconEdit size={12} /></button>
                          <button type="button" onClick={() => void del(l.id)} data-tooltip="Удалить" className="shrink-0 text-[var(--textMuted)]/60 hover:text-[#ef4444]"><IconTrash size={12} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {links.length === 0 && editing === null && (
              <Empty compact title="Пока пусто" text="добавьте первый материал." />
            )}
          </div>
          </>)}
        </div>

      {openDoc && (
        <KbDocModal docId={openDoc.id} presetScope={openDoc.scope ?? "personal"} presetCategory={openDoc.category ?? ""}
          parentId={openDoc.parentId} anchor={openDoc.rect ?? null} categories={allCats}
          onClose={() => setOpenDoc(null)} onSaved={() => reload()} />
      )}
    </div>
  );
}
